import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import { extractOcrService } from "../../../services/ocr.service";
import LoaderSpinner from "../../common/Loader";
import "./Ocr.css";

const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
const PDF_TYPE = "application/pdf";
const ALL_PAGES = "all";
const LINE_COLOR = "rgba(27, 118, 175, 0.95)";
const BARCODE_COLOR = "rgba(30, 133, 75, 0.95)";
const MIN_LINE_SCORE = 0.5;
const ADDRESS_KEYS = ["Street", "CityStateZip", "Zip", "Country"];

// Drops lone characters and low-confidence fragments, keeping the detector reading order.
export const buildCleanText = (lines) =>
  (lines || [])
    .filter((l) => (l.text || "").trim().length >= 2 && (l.score || 0) >= MIN_LINE_SCORE)
    .map((l) => l.text.trim())
    .join("\n");

export const buildCleanedSectionText = (fields, lines) => {
  const parts = [];
  if (fields?.Tracking) parts.push(`Tracking: ${fields.Tracking}`);
  const address = ADDRESS_KEYS.map((k) => fields?.[k]).filter(Boolean);
  if (address.length) parts.push(`Address:\n${address.join("\n")}`);
  const clean = buildCleanText(lines);
  if (clean) parts.push(clean);
  return parts.join("\n\n");
};

export const buildFieldsText = (fields) =>
  Object.entries(fields || {})
    .map(([k, v]) => `${k}: ${v}`)
    .join("\n");

// navigator.clipboard is unavailable on non-secure (http) origins, so fall back to execCommand.
const copyToClipboard = async (text) => {
  if (navigator.clipboard?.writeText && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  const copied = document.execCommand("copy");
  textarea.remove();
  if (!copied) throw new Error("Copy failed");
};

const downloadTextFile = (text, fileName) => {
  const url = URL.createObjectURL(new Blob([text], { type: "text/plain;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const SectionHeader = ({ title, text, downloadName, className = "" }) => {
  const handleCopy = async () => {
    try {
      await copyToClipboard(text);
      toast.success(`${title} copied to clipboard.`);
    } catch (e) {
      toast.error("Unable to copy to clipboard.");
    }
  };

  return (
    <div className={`ocrSectionHeader ${className}`}>
      <h2 className="ocrHeading">{title}</h2>
      <div className="ocrSectionActions">
        <button type="button" className="btn cus-seconday-bg-btn ocrActionBtn" onClick={handleCopy} disabled={!text}>
          Copy
        </button>
        <button
          type="button"
          className="btn cus-seconday-bg-btn ocrActionBtn"
          onClick={() => downloadTextFile(text, downloadName)}
          disabled={!text}
        >
          Download.txt
        </button>
      </div>
    </div>
  );
};

SectionHeader.propTypes = {
  title: PropTypes.string.isRequired,
  text: PropTypes.string,
  downloadName: PropTypes.string.isRequired,
  className: PropTypes.string,
};

const getErrorMessage = (error) => {
  const data = error?.response?.data;
  if (typeof data === "string" && data) return data;
  return data?.Error || data?.error || error?.message || "Failed to extract text from the file.";
};

const Ocr = () => {
  const [dragOver, setDragOver] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fileName, setFileName] = useState("");
  const [imageUrl, setImageUrl] = useState(null);
  const [isPdfFile, setIsPdfFile] = useState(false);
  const [selectedPage, setSelectedPage] = useState(ALL_PAGES);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    return () => {
      if (imageUrl) URL.revokeObjectURL(imageUrl);
    };
  }, [imageUrl]);

  const drawBoxes = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !imageUrl) return;
    const img = new Image();
    img.onload = () => {
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      const lineWidth = Math.max(2, img.naturalWidth / 400);

      ctx.lineWidth = lineWidth;
      ctx.strokeStyle = LINE_COLOR;
      (result?.lines || []).forEach((l) => {
        if (Array.isArray(l.box)) ctx.strokeRect(...l.box);
      });

      ctx.lineWidth = lineWidth * 1.5;
      ctx.strokeStyle = BARCODE_COLOR;
      (result?.barcodes || []).forEach((b) => {
        if (Array.isArray(b.box)) ctx.strokeRect(...b.box);
      });
    };
    img.src = imageUrl;
  }, [imageUrl, result]);

  useEffect(() => {
    drawBoxes();
  }, [drawBoxes]);

  const handleFile = async (file) => {
    if (!file || loading) return;
    if (!file.type.startsWith("image/") && file.type !== PDF_TYPE) {
      toast.error("Please select an image or PDF file.");
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      toast.error("File exceeds the 10 MB limit.");
      return;
    }

    setFileName(file.name);
    setResult(null);
    setSelectedPage(ALL_PAGES);
    setIsPdfFile(file.type === PDF_TYPE);
    setImageUrl(URL.createObjectURL(file));
    setLoading(true);
    try {
      const response = await extractOcrService(file);
      setResult(response.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const onDrop = (event) => {
    event.preventDefault();
    setDragOver(false);
    handleFile(event.dataTransfer.files[0]);
  };

  const onDragOver = (event) => {
    event.preventDefault();
    setDragOver(true);
  };

  const clearAll = () => {
    setImageUrl(null);
    setIsPdfFile(false);
    setSelectedPage(ALL_PAGES);
    setResult(null);
    setFileName("");
  };

  // A PDF reply carries per-page results; the selected page (or the combined reply) drives the panels.
  const pages = result?.pages || [];
  const selectedIndex = selectedPage === ALL_PAGES ? -1 : Number(selectedPage);
  const view = pages[selectedIndex] || result;
  const fields = view?.fields || {};
  const fieldEntries = Object.entries(fields);
  const addressLines = ADDRESS_KEYS.map((k) => fields[k]).filter(Boolean);
  const cleanText = buildCleanText(view?.lines);
  const baseName = (fileName || "ocr").replace(/\.[^.]+$/, "");

  let dropText = "Drop an image or PDF here, or click to choose";
  if (loading) dropText = `Reading ${fileName} …`;
  else if (fileName) dropText = "Drop another image or PDF, or click to choose";

  return (
    <main>
      {loading && <LoaderSpinner />}
      <div className="main__container">
        <div className="main__tile_heading cus-btn-include-sec mb-3">
          <span>
            OCR
            <div className="infoPermission">
              <i className="fa fa-lightbulb-o editorIdea" aria-hidden="true"></i>{" "}
              Upload a label or tag image or PDF. For images, highlighted boxes show detected text lines (blue) and barcodes (green).
            </div>
          </span>
          {imageUrl && (
            <button className="btn cus-seconday-bg-btn top-btn" onClick={clearAll} disabled={loading}>
              Clear
            </button>
          )}
        </div>

        <div className="cus-page-content-sec">
          <div className="ocrLayout">
            <section className="ocrCard">
              <button
                type="button"
                className={`ocrDrop ${dragOver ? "hover" : ""}`}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={onDragOver}
                onDragEnter={onDragOver}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                data-testid="ocr-drop"
              >
                {dropText}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,application/pdf"
                hidden
                data-testid="ocr-file-input"
                onChange={(e) => handleFile(e.target.files[0])}
              />
              {imageUrl && !isPdfFile && <canvas ref={canvasRef} className="ocrCanvas" />}
              {imageUrl && isPdfFile && (
                <object data={imageUrl} type={PDF_TYPE} className="ocrPdf" data-testid="ocr-pdf-preview">
                  <div className="ocrEmpty">PDF preview is not available in this browser.</div>
                </object>
              )}
            </section>

            <section className="ocrCard">
              <div className="ocrMeta">
                <span className="ocrPill">Type: <b>{view?.labelType ?? "—"}</b></span>
                <span className="ocrPill">
                  Confidence: <b>{view ? `${((view.confidence || 0) * 100).toFixed(1)}%` : "—"}</b>
                </span>
                <span className="ocrPill">Lines: <b>{view ? (view.lines || []).length : "—"}</b></span>
                {pages.length > 1 && (
                  <select
                    className="ocrPageSelect"
                    aria-label="Page"
                    data-testid="ocr-page-select"
                    value={selectedPage}
                    onChange={(e) => setSelectedPage(e.target.value)}
                  >
                    <option value={ALL_PAGES}>All pages ({pages.length})</option>
                    {pages.map((p, i) => (
                      <option key={p.page ?? i} value={String(i)}>
                        {`Page ${p.page ?? i + 1} (${p.source === "text" ? "PDF text" : "OCR"})`}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <SectionHeader
                title="Cleaned Text"
                text={result ? buildCleanedSectionText(fields, view.lines) : ""}
                downloadName={`${baseName}-cleaned-text.txt`}
              />
              {result ? (
                <div className="ocrReadable">
                  {fields.Tracking && (
                    <div className="ocrRow">
                      <span className="ocrLabel">Tracking</span>
                      <span className="ocrBig">{fields.Tracking}</span>
                    </div>
                  )}
                  {addressLines.length > 0 && (
                    <div className="ocrRow">
                      <span className="ocrLabel">Address</span>
                      <span>
                        {addressLines.map((a) => (
                          <div key={a}>{a}</div>
                        ))}
                      </span>
                    </div>
                  )}
                  {cleanText ? (
                    <div className="ocrClean">{cleanText}</div>
                  ) : (
                    <div className="ocrEmpty">Nothing readable detected.</div>
                  )}
                </div>
              ) : (
                <div className="ocrEmpty">{loading ? "Extracting…" : "No image yet."}</div>
              )}

              <SectionHeader
                className="mt-3"
                title="Parsed Fields"
                text={buildFieldsText(fields)}
                downloadName={`${baseName}-parsed-fields.txt`}
              />
              {!result && <div className="ocrEmpty">No image yet.</div>}
              {result && fieldEntries.length === 0 && (
                <div className="ocrEmpty">No structured fields (unrecognized label type).</div>
              )}
              {fieldEntries.length > 0 && (
                <table className="table ocrFields">
                  <tbody>
                    {fieldEntries.map(([k, v]) => (
                      <tr key={k}>
                        <td className="ocrKey">{k}</td>
                        <td>{String(v)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {view?.barcodes?.length > 0 && (
                <>
                  <h2 className="ocrHeading mt-3">Barcodes</h2>
                  <table className="table ocrFields">
                    <tbody>
                      {view.barcodes.map((b, i) => (
                        <tr key={`${b.text}-${i}`}>
                          <td className="ocrKey">{b.format || "Barcode"}</td>
                          <td>{b.text}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}

              <SectionHeader
                className="mt-3"
                title="Raw Text"
                text={view?.rawText || ""}
                downloadName={`${baseName}-raw-text.txt`}
              />
              <pre className="ocrRaw">{result ? view.rawText || "(no text detected)" : "No image yet."}</pre>
            </section>
          </div>
        </div>
      </div>
    </main>
  );
};

export default Ocr;
